import { Autocomplete, Chip, TextField } from "@mui/material";
import { isValidEmail } from "@vmosolutions/oc-interfaces";

interface IEmailChipFieldProps {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** When true, an empty list is allowed; otherwise the field shows nothing special (validation is parent-driven). */
  allowEmpty?: boolean;
}

/**
 * Free-text email-chip input shared by the GenDec global settings, the per-station
 * recipient lists, and any future place that needs a "list of emails" field. Invalid
 * entries get a red chip + helper text count; the parent is still responsible for
 * gating save via the shared `validateGendecConfig` / `validateStationConfig`.
 */
export const EmailChipField = (props: IEmailChipFieldProps): JSX.Element => {
  const { label, value, onChange, placeholder = "Add email and press Enter", disabled = false } = props;
  const invalidCount = value.filter((v) => !isValidEmail(v)).length;
  return (
    <Autocomplete
      multiple
      freeSolo
      disabled={disabled}
      options={[]}
      value={value}
      onChange={(_e, next) => onChange(next as string[])}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => {
          const { key: tagKey, ...tagProps } = getTagProps({ index });
          const valid = isValidEmail(option);
          return (
            <Chip
              key={tagKey}
              size="small"
              variant="outlined"
              color={valid ? "default" : "error"}
              label={option}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder={placeholder}
          error={invalidCount > 0}
          helperText={invalidCount > 0 ? `${invalidCount} invalid email${invalidCount === 1 ? "" : "s"}` : " "}
        />
      )}
    />
  );
};

export default EmailChipField;
